import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../helpers/axiosInstance';
import { extractErrorMessage } from '../helpers/errorMessage';

export const loadQueue = createAsyncThunk(
  'underwriter/loadQueue',
  async ({ view = 'all', search = '', page = 1 } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/underwriter/queue', {
        params: { view, search: search || undefined, page }
      });
      return {
        items: data.items,
        total: data.total,
        page: data.page ?? page,
        pageSize: data.pageSize ?? 25,
        view
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const loadCounts = createAsyncThunk(
  'underwriter/loadCounts',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/underwriter/counts');
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const loadApplication = createAsyncThunk(
  'underwriter/loadApplication',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/underwriter/applications/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const loadAudit = createAsyncThunk(
  'underwriter/loadAudit',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/underwriter/applications/${id}/audit`);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const pickUpApplication = createAsyncThunk(
  'underwriter/pickUp',
  async (id, { rejectWithValue }) => {
    try {
      // Pick up the application (assigns it to this underwriter).
      await axiosInstance.post(`/underwriter/applications/${id}/pick-up`);

      // Re-fetch the FULL application so we keep riskAssessment, the rule
      // assessment, audit history — everything the detail screen needs.
      // The pick-up endpoint returns a lighter object without these.
      const { data } = await axiosInstance.get(`/underwriter/applications/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const releaseApplication = createAsyncThunk(
  'underwriter/release',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`/underwriter/applications/${id}/release`);
      const { data } = await axiosInstance.get(`/underwriter/applications/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const decideApplication = createAsyncThunk(
  'underwriter/decide',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(`/underwriter/applications/${id}/decide`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

// Fetches a viewable link (S3) or blob (local) for a document.
export const openDocument = createAsyncThunk(
  'underwriter/openDocument',
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/documents/${documentId}/view`, {
        responseType: 'blob'
      });

      // S3 driver returns JSON with a signed URL; local returns raw bytes.
      const isJson = response.data.type === 'application/json';
      if (isJson) {
        const text = await response.data.text();
        return JSON.parse(text).data.url;
      }
      return URL.createObjectURL(response.data);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

const initialState = {
  queue: [],
  total: 0,
  page: 1,
  pageSize: 25,
  view: 'all',
  counts: { submitted: 0, underReview: 0, moreInfo: 0, approved: 0, rejected: 0 },
  current: null,
  audit: [],
  loadingQueue: false,
  loadingApplication: false,
  acting: false,
  error: null,
  actionError: null
};

const underwriterSlice = createSlice({
  name: 'underwriter',
  initialState,

  reducers: {
    clearUnderwriterError: (state) => {
      state.error = null;
      state.actionError = null;
    },
    clearCurrent: (state) => {
      state.current = null;
      state.audit = [];
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadQueue.pending, (state) => {
        state.loadingQueue = true;
        state.error = null;
      })
      .addCase(loadQueue.fulfilled, (state, action) => {
        state.loadingQueue = false;
        state.queue = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.view = action.payload.view;
      })
      .addCase(loadQueue.rejected, (state, action) => {
        state.loadingQueue = false;
        state.error = action.payload;
      })

      .addCase(loadCounts.fulfilled, (state, action) => {
        state.counts = action.payload;
      })

      .addCase(loadApplication.pending, (state) => {
        state.loadingApplication = true;
        state.error = null;
      })
      .addCase(loadApplication.fulfilled, (state, action) => {
        state.loadingApplication = false;
        state.current = action.payload;
      })
      .addCase(loadApplication.rejected, (state, action) => {
        state.loadingApplication = false;
        state.error = action.payload;
      })

      .addCase(loadAudit.fulfilled, (state, action) => {
        state.audit = action.payload;
      })

      .addCase(pickUpApplication.pending, (state) => {
        state.acting = true;
        state.actionError = null;
      })
      .addCase(pickUpApplication.fulfilled, (state, action) => {
        state.acting = false;
        state.current = action.payload;
      })
      .addCase(pickUpApplication.rejected, (state, action) => {
        state.acting = false;
        state.actionError = action.payload;
      })

      .addCase(releaseApplication.pending, (state) => {
        state.acting = true;
        state.actionError = null;
      })
      .addCase(releaseApplication.fulfilled, (state, action) => {
        state.acting = false;
        state.current = action.payload;
      })
      .addCase(releaseApplication.rejected, (state, action) => {
        state.acting = false;
        state.actionError = action.payload;
      })

      .addCase(decideApplication.pending, (state) => {
        state.acting = true;
        state.actionError = null;
      })
      .addCase(decideApplication.fulfilled, (state, action) => {
        state.acting = false;
        state.current = action.payload;
      })
      .addCase(decideApplication.rejected, (state, action) => {
        state.acting = false;
        state.actionError = action.payload;
      });
  }
});

export const { clearUnderwriterError, clearCurrent } = underwriterSlice.actions;

export const selectQueue = (state) => state.underwriter.queue;
export const selectQueueTotal = (state) => state.underwriter.total;
export const selectQueueView = (state) => state.underwriter.view;
export const selectCounts = (state) => state.underwriter.counts;
export const selectCurrentApplication = (state) => state.underwriter.current;
export const selectAudit = (state) => state.underwriter.audit;
export const selectLoadingQueue = (state) => state.underwriter.loadingQueue;
export const selectLoadingApplication = (state) => state.underwriter.loadingApplication;
export const selectActing = (state) => state.underwriter.acting;
export const selectUwError = (state) => state.underwriter.error;
export const selectActionError = (state) => state.underwriter.actionError;
export const selectQueuePage = (state) => state.underwriter.page;
export const selectQueuePageSize = (state) => state.underwriter.pageSize;

export default underwriterSlice.reducer;
